import { useLocation } from 'react-router';

import type { PrefetchContext, PrefetchedDataProps } from 'src/shared/prefetch';

import { useCountryCode, useLocale } from 'src/app/shared/hooks/l10n';
import { useQueryParamValue } from 'src/app/shared/hooks/location';
import { useCurrentUser, useLoginState } from 'src/app/shared/hooks/session';
import { PSF_COMBO_QUERY_PARAM_DATA_NAME } from 'src/app/shared/utils/bandits';

import { prefetchPromotePageDataFromApi, usePromotePageDataFromApi } from './pageData';

export type PromotePagePrefetchedContext = Readonly<{
	pageData: Parameters<typeof usePromotePageDataFromApi>[0];
}>;

export type PromotePageCombinedState = ReturnType<typeof usePromotePageDataFromApi>;

export function usePageContext(slug: string, prefetchedData?: PromotePagePrefetchedContext): PromotePageCombinedState {
	const countryCode = useCountryCode();
	const locale = useLocale();
	const comboPageBanditQueryParam = useQueryParamValue(PSF_COMBO_QUERY_PARAM_DATA_NAME);
	const psfComboBanditStaticAmount = useQueryParamValue('psf_combo_static_amount');

	const { pathname } = useLocation();
	const loginState = useLoginState();
	const user = useCurrentUser();

	return usePromotePageDataFromApi(
		prefetchedData?.pageData,
		{
			slug,
			countryCode,
			locale,
			path: pathname,
			loginState,
			stateCode: user?.stateCode || undefined,
		},
		comboPageBanditQueryParam,
		psfComboBanditStaticAmount,
	);
}

export async function prefetchPageContext(
	slug: string,
	context: PrefetchContext,
): Promise<PromotePagePrefetchedContext> {
	const {
		l10n: { countryCode, locale },
		session,
		query,
		path,
	} = context;

	if (!session) throw new Error('Unexpected missing session');

	const { loginState, user } = session;
	const comboPageBanditQueryParam =
		typeof query[PSF_COMBO_QUERY_PARAM_DATA_NAME] === 'string' ? query[PSF_COMBO_QUERY_PARAM_DATA_NAME] : undefined;
	const psfComboBanditStaticAmount =
		typeof query.psf_combo_static_amount === 'string' ? query.psf_combo_static_amount : undefined;

	const pageData = await prefetchPromotePageDataFromApi(
		context,
		{
			slug,
			countryCode,
			locale,
			path,
			loginState,
			stateCode: user?.stateCode || undefined,
		},
		comboPageBanditQueryParam,
		psfComboBanditStaticAmount,
	);

	return { pageData };
}

export type PromotePagePrefetchedDataProps = PrefetchedDataProps<PromotePagePrefetchedContext>;
